import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Search, ShieldOff, ShieldCheck, ToggleLeft, ToggleRight,
  Users, AlertTriangle, CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { SyncDataButton } from "../components/SyncDataButton";
import { CopyButton } from "../components/CopyField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublisherExclusionsPage() {
  const [publishers, setPublishers] = useState([]);
  const [excludedSet, setExcludedSet] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [ringbaUnavailable, setRingbaUnavailable] = useState(false);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [pubRes, exclRes] = await Promise.all([
        axios.get(`${API}/publisher-control/names`, { headers }),
        axios.get(`${API}/publishers/exclusions`, { headers }),
      ]);
      setPublishers(pubRes.data.publishers || []);
      setRingbaUnavailable(!!pubRes.data.ringba_unavailable);
      setExcludedSet(new Set(exclRes.data.excluded || []));
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to fetch publisher data:", err);
      if (!silent) toast.error("Failed to load publisher data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 30s auto-refresh
  useEffect(() => {
    intervalRef.current = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/publisher-control/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchData();
      toast.success("Exclusions synced");
    } finally {
      setSyncing(false);
    }
  };

  const toggleExclusion = async (pubName) => {
    setToggling(pubName);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/publishers/exclusions/toggle`,
        { publisher_name: pubName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExcludedSet((prev) => {
        const next = new Set(prev);
        if (res.data.excluded) {
          next.add(pubName);
          toast.success(`${pubName} excluded from analysis`);
        } else {
          next.delete(pubName);
          toast.success(`${pubName} included in analysis`);
        }
        return next;
      });
    } catch (err) {
      toast.error("Failed to toggle publisher status");
    } finally {
      setToggling(null);
    }
  };

  const filtered = publishers.filter((p) =>
    p.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = publishers.length - excludedSet.size;
  const excludedCount = excludedSet.size;

  return (
    <div className="space-y-6" data-testid="publisher-exclusions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Publisher Exclusions
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Control which publishers appear in Call Analysis and reports
            {lastUpdated && <span className="ml-1 sm:ml-2 text-slate-600">&middot; Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-[11px] font-semibold text-emerald-400 tracking-wide uppercase">Live</span>
          </div>
          <SyncDataButton onSync={handleSync} syncing={syncing} lastSynced={lastUpdated} ringbaUnavailable={ringbaUnavailable} testId="sync-data-btn" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/15">
              <Users className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Publishers</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{publishers.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/15">
              <CheckCircle className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active</p>
              <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "Outfit, sans-serif" }}>{activeCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#11141D] border-white/5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/15">
              <ShieldOff className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Excluded</p>
              <p className="text-2xl font-bold text-red-400" style={{ fontFamily: "Outfit, sans-serif" }}>{excludedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Search publishers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-[#11141D] border-white/5 text-slate-200 placeholder:text-slate-600"
          data-testid="publisher-search"
        />
      </div>

      {/* Publisher List */}
      <Card className="bg-[#11141D] border-white/5">
        <CardHeader className="pb-3 border-b border-white/5">
          <CardTitle className="text-sm font-semibold text-slate-200" style={{ fontFamily: "Outfit, sans-serif" }}>
            Publishers ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-blue-500 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-500">
              {search ? "No publishers match your search" : "No publishers found"}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map((pubName) => {
                const isExcluded = excludedSet.has(pubName);
                const isToggling = toggling === pubName;
                return (
                  <div
                    key={pubName}
                    className={`flex items-center justify-between px-5 py-3.5 transition-all duration-200 ${
                      isExcluded ? "bg-red-500/[0.03]" : "hover:bg-white/[0.02]"
                    }`}
                    data-testid={`publisher-row-${pubName.replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-1.5 rounded-lg ${isExcluded ? "bg-red-500/10" : "bg-emerald-500/10"}`}>
                        {isExcluded
                          ? <ShieldOff className="h-4 w-4 text-red-400" />
                          : <ShieldCheck className="h-4 w-4 text-emerald-400" />
                        }
                      </div>
                      <span className={`group inline-flex items-center gap-1 min-w-0 text-sm font-medium ${isExcluded ? "text-slate-500" : "text-slate-200"}`}>
                        <span className={`truncate ${isExcluded ? "line-through" : ""}`}>{pubName}</span>
                        <CopyButton text={pubName} label="publisher name" testId={`copy-name-${pubName.replace(/\s+/g, "-")}`} />
                      </span>
                      {isExcluded && (
                        <Badge className="bg-red-500/10 text-red-400 border-0 text-[10px] px-1.5 py-0 shrink-0">
                          Excluded
                        </Badge>
                      )}
                    </div>
                    <button
                      onClick={() => toggleExclusion(pubName)}
                      disabled={isToggling}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                        isExcluded
                          ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20"
                          : "bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                      } disabled:opacity-50`}
                      data-testid={`toggle-${pubName.replace(/\s+/g, "-")}`}
                    >
                      {isToggling ? (
                        <div className="animate-spin rounded-full h-3 w-3 border border-current border-t-transparent" />
                      ) : isExcluded ? (
                        <ToggleRight className="h-3.5 w-3.5" />
                      ) : (
                        <ToggleLeft className="h-3.5 w-3.5" />
                      )}
                      {isExcluded ? "Include" : "Exclude"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      {excludedCount > 0 && (
        <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/10 rounded-xl p-4" data-testid="exclusion-warning">
          <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-300/80 leading-relaxed">
            <strong>{excludedCount} publisher{excludedCount > 1 ? "s" : ""}</strong> excluded.
            Their calls will not appear in Call Analysis, Dashboard, Analytics, Publisher Report, or Publisher Ranking.
          </p>
        </div>
      )}
    </div>
  );
}
